import React, { FC, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

import { ReactComponent as LockIcon } from "assets/images/iconsV2/lock-01.svg";
import Avatar from "components/v2/Avatar/Avatar";
import usePermissions from "hooks/usePermissions";
import SupportingText from "pages/common/ShipmentTrackingV2/components/Activity/components/ActivityItem/SupportingText";
import Files from "pages/common/ShipmentTrackingV2/components/Activity/components/ActivityItem/Files";
import "./CommentItem.scss";

type CommentItemProps = {
  logo?: string;
  authorName?: string;
  companyName?: string;
  timestamp?: string;
  customMessage?: React.ReactNode;
  message?: string;
  isUserComment?: boolean;
  files?: any;
  deletedAttachments?: any;
  audience?: string;
};

const CommentItem: FC<CommentItemProps> = function (props) {
  const {
    logo,
    authorName,
    companyName,
    timestamp,
    customMessage,
    message,
    isUserComment,
    files,
    deletedAttachments,
    audience,
  } = props;

  const intl = useIntl();
  const { permissions } = usePermissions();
  const { company_name, user_type } = JSON.parse(permissions || "{}");
  const isProvider = user_type === "provider";

  const isSelfCompany = companyName === audience;
  let showLockIcon = false;

  const getAudienceLabel = useCallback(() => {
    if (!audience) return;
    if (audience === "all")
      return intl.formatMessage({
        id: "quote-request.comments.visibleToEveryone",
        defaultMessage: "Visible to everyone",
      });

    if (isSelfCompany) {
      showLockIcon = true;

      return intl.formatMessage(
        {
          id: "quote-request.comments.visibleToInternalUsers",
          defaultMessage: "Visible to internal users",
        },
        {
          b: (chunks) => <strong>{chunks}</strong>,
        },
      );
    }

    // If provider-view or provider sends comment to shipper
    if (isProvider || company_name === audience) return "";

    showLockIcon = true;
    return intl.formatMessage(
      {
        id: "quote-request.comments.onlyVisibleTo",
        defaultMessage: "only visible to <b>{companyName}</b>",
      },
      {
        companyName: audience,
        b: (chunks) => <strong>{chunks}</strong>,
      },
    );
  }, [audience, isProvider]);

  const audienceLabel = getAudienceLabel();

  return (
    <div className="d-flex comment-item">
      <div className="d-flex flex-column align-items-center">
        <Avatar image={logo} name={authorName} />
        <div className="comment-item--connector" />
      </div>

      <div className="ml-lg pb-4xl flex-1">
        <div className="d-flex flex-wrap gap-x-md align-items-center">
          <p className="m-0 text-sm font-medium">
            {authorName}
            {authorName && companyName && " • "}
            <span className="font-regular">{companyName}</span>
          </p>

          {timestamp && <p className="m-0 text-xs text-secondary">{timestamp}</p>}
        </div>

        {customMessage && customMessage}

        {message ? (
          <>
            {isUserComment ? (
              <>
                <div className="d-flex align-items-center mb-md">
                  {showLockIcon && <LockIcon className="mr-xs" />}
                  <SupportingText>
                    <FormattedMessage id="tracking.activity.leftAComment" defaultMessage="Left a comment" />
                    {audienceLabel && <span>, {audienceLabel}</span>}
                  </SupportingText>
                </div>

                <div className="comment-item--comment-container">
                  <ReactMarkdown className="text-md pre-line" rehypePlugins={[rehypeRaw]}>
                    {message}
                    {/* To allow multiple new lines */}
                    {/* {message.replace(/\n/gi, "&nbsp;\n")} */}
                  </ReactMarkdown>
                  {files && <Files files={files} deleted_attachments={deletedAttachments} is_user_comment={isUserComment} />}
                </div>
              </>
            ) : (
              <>
                <SupportingText className="pre-line">{message}</SupportingText>
                <Files files={files} deleted_attachments={deletedAttachments} is_user_comment={isUserComment} />
              </>
            )}
          </>
        ) : (
          <Files files={files} deleted_attachments={deletedAttachments} />
        )}
      </div>
    </div>
  );
};

export default CommentItem;

CommentItem.defaultProps = {
  authorName: undefined,
  logo: undefined,
  companyName: undefined,
  timestamp: undefined,
  customMessage: undefined,
  message: undefined,
  isUserComment: false,
  files: undefined,
  deletedAttachments: undefined,
  audience: undefined,
};
